import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar"
import { Stack } from "@fluentui/react/lib/Stack"
import { IProcessedStyleSet } from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React, { ForwardedRef, useEffect, useState } from "react"

import * as reactConfiguratorImport from "@encoway/react-configurator"
import { Http } from "@encoway/sales-showroom-auth"

import {
    IAppSettings,
    IAppState,
    loadTranslations,
    mergeDefaultSettings,
    registerAliases
} from "../../appUtil"
import { ComponentName } from "../../constants"
import { VisualizationService } from "../../services/visualization/visualizationService"
import { IVisualEditor } from "../../services/visualization/visualizationService.types"
import { VisualizationWrapper } from "../VisualizationWrapper/VisualizationWrapper"
import { AssemblyAppStyles } from "./AssemblyApp.styles"
import { IAssemblyAppProps, IAssemblyAppStyles } from "./AssemblyApp.types"

const IAssemblyApp = (
    props: IAssemblyAppProps,
    visualEditorForwardRef: ForwardedRef<IVisualEditor>
) => {
    const { theme, styles } = props
    const [state, setState] = useState({
        initialized: false,
        settings: {}
    } as IAppState)
    const [settingsChanged, setSettingsChanged] = useState(false)
    const [busyOverlay, setBusyOverlay] = useState(false)
    const [savedAssemblyState, setSavedAssemblyState] = useState(undefined)

    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<IAssemblyAppStyles>
    const reactConfigurator = props.reactConfigurator || reactConfiguratorImport

    useEffect(() => {
        if (state?.initialized) {
            setSettingsChanged(true)
        }
    }, [props.settings])

    // reload everything on start
    useEffect(() => {
        const load = async () => {
            const { configurationService, initialized } = state
            const updateRequired = !initialized || settingsChanged
            let appState: IAppState = {
                configurationService,
                settings: {
                    theme: props.theme,
                    ...(await props.settings)
                } as IAppSettings
            }
            let error = null
            if (updateRequired) {
                try {
                    appState = await mergeDefaultSettings(appState)
                    appState = await loadTranslations(appState)
                } catch (e) {
                    error = e
                }
                registerAliases(appState)
                const assemblySettings =
                    appState.settings.visualization?.settings?.assembly
                const configurationSettings = appState.settings.configuration
                if (
                    configurationSettings?.baseUrl &&
                    assemblySettings?.lineItemId &&
                    assemblySettings?.sdmiId
                ) {
                    const assemblyState =
                        await VisualizationService.loadAssemblyState(
                            Http.create(configurationSettings),
                            configurationSettings.baseUrl,
                            assemblySettings.lineItemId,
                            assemblySettings.sdmiId
                        )
                    setSavedAssemblyState(assemblyState)
                }

                setState({
                    ...appState,
                    initialized: true,
                    error
                })
                if (settingsChanged) {
                    setSettingsChanged(false)
                }
            }
        }
        load()
    }, [settingsChanged])

    const ComponentFactory = reactConfigurator.ComponentFactory

    if (!state.initialized) {
        return (
            <div className={classNames.loadingRoot}>
                {ComponentFactory.instanceOf(
                    ComponentName.InitialLoadingSpinner,
                    {
                        show: true,
                        label: new URLSearchParams(window.location.search).get(
                            "product"
                        )
                    }
                )}
            </div>
        )
    }
    if (state.error) {
        return (
            <Stack style={{ flex: 1 }}>
                <MessageBar
                    messageBarType={MessageBarType.error}
                    style={{ zIndex: 10000000000 }}
                >
                    {state.error.message ||
                        "Some error occurred. Please contact your administrator."}
                </MessageBar>
                <div className={classNames.loadingRoot}>
                    {ComponentFactory.instanceOf(
                        ComponentName.InitialLoadingSpinner,
                        {
                            show: true,
                            label: new URLSearchParams(
                                window.location.search
                            ).get("product")
                        }
                    )}
                </div>
            </Stack>
        )
    }

    return (
        <div style={{ width: "100%", height: "100%" }}>
            <VisualizationWrapper
                theme={theme}
                styles={styles}
                classNames={classNames}
                eventBus={props.eventBus}
                visualizationProvider={props.visualizationProvider}
                reactConfigurator={reactConfigurator}
                settings={state.settings.visualization}
                configurationSettings={state.settings.configuration}
                configurationService={state.configurationService}
                setBusyOverlay={setBusyOverlay}
                readOnly={state.settings.configuration.options.readOnly}
                visualizationState={savedAssemblyState}
                visualEditorRef={visualEditorForwardRef}
            />
            {(busyOverlay || settingsChanged) &&
                ComponentFactory.instanceOf(ComponentName.BusyOverlay)}
        </div>
    )
}

const IAssemblyAppWithVisualEditorForwardRef = React.forwardRef(
    (
        props: IAssemblyAppProps,
        visualEditorForwardRef: ForwardedRef<IVisualEditor>
    ) => IAssemblyApp(props, visualEditorForwardRef)
)
IAssemblyAppWithVisualEditorForwardRef.displayName = "AssemblyApp"

export const AssemblyApp = styled(
    IAssemblyAppWithVisualEditorForwardRef,
    AssemblyAppStyles
)
AssemblyApp.displayName = "AssemblyApp"
